<template>
    <div>
        <h2>{{ step.title }}</h2>
        <p>If you need to leave this page, your answers will be saved for 24 hours.</p>
        <p class="step__notice">
            All fields with an asterisk are required
            <i class="fas fa-asterisk"></i>
        </p>
        <p>Please tell us about your health and personal support needs.
            <b>Be honest and share as much information as you can - the more we understand, the better we can help you.  
            </b>
        </p>

        <InputCheckboxGroup
            name="any_illness"
            :required="checkIsRequired('any_illness')"
            :error="formRef.errors.get('any_illness')"
            v-model="formRef.any_illness"
            :form="formRef"
            :options="getField('any_illness')['options']"
            @textarea-updated="formRef[$event.field] = $event.value"
            @update:modelValue="handleAffectedByInput"
        >
            <template v-slot:question>
                {{ getField("any_illness").question }}
            </template>
            <template v-slot:description>
                <small class="d-block mb-3">
                    Examples of mental health or psychological conditions include:
                </small>
                <ul>
                    <li>Anxiety</li>
                    <li>Depression</li>
                    <li>Bipolar</li>
                    <li>Obsessive compulsive disorder</li>
                    <li>Personality disorder</li>
                    <li>Eating disorder</li>
                    <li>ADHD</li>
                    <li>Self-harm</li>
                </ul>
                <small class="d-block mb-3">Examples of disabilities and long-term illnesses can include:</small>
                <ul>
                    <li>Needing a wheelchair</li>
                    <li>Severe asthma</li>
                    <li>Dyslexia</li>
                    <li>Epilepsy</li>
                    <li>Myalgic encephalomyelitis (ME)</li>
                    <li>Autism (including Asperger’s Syndrome)</li>
                    <li>Diabetes</li>
                    <li>Learning difficulties</li>
                </ul>
                <small class="d-block mb-3">
                    Not sure how to answer this question?
                    <a
                        href="https://www.citizensadvice.org.uk/work/discrimination-at-work/checking-if-its-discrimination/check-if-youre-disabled-under-the-equality-act"
                        target="_blank"
                    >
                        Check if you’re disabled under the equality act
                    </a>
                </small>
                <small class="d-block mb-3">Select all that apply</small>
            </template>
        </InputCheckboxGroup>

        <InputText
            v-if="formRef.any_illness && formRef.any_illness.includes('long_term_illness')"
            name="illness_reason_illness_text"
            :required="checkIsRequired('illness_reason_illness_text')"
            :error="formRef.errors.get('illness_reason_illness_text')"
            v-model="formRef.illness_reason_illness_text"
            :options="getField('illness_reason_illness_text')['options']"
            :form="formRef"
            @textarea-updated="formRef[$event.field] = $event.value"
        >   
            <template v-slot:question>
                {{ getField("illness_reason_illness_text").question }}
            </template>
        </InputText>

        <InputText
            v-if="formRef.any_illness && formRef.any_illness.includes('disability')"
            name="illness_reason_disability_text"
            :required="checkIsRequired('illness_reason_disability_text')"
            :error="formRef.errors.get('illness_reason_disability_text')"
            v-model="formRef.illness_reason_disability_text"
            :options="getField('illness_reason_disability_text')['options']"
            :form="formRef"
            @textarea-updated="form[$event.field] = $event.value"
        >   
            <template v-slot:question>
                {{ getField("illness_reason_disability_text").question }}
            </template>
        </InputText>

        <InputText
            v-if="formRef.any_illness && formRef.any_illness.includes('mental_health_challenges')"
            name="illness_reason_mental_health_psychological_text"
            :required="checkIsRequired('illness_reason_mental_health_psychological_text')"
            :error="formRef.errors.get('illness_reason_mental_health_psychological_text')"
            v-model="formRef.illness_reason_mental_health_psychological_text"
            :options="getField('illness_reason_mental_health_psychological_text')['options']"
            :form="formRef"
            @textarea-updated="form[$event.field] = $event.value"
        >   
            <template v-slot:question>
                {{ getField("illness_reason_mental_health_psychological_text").question }}
            </template>
        </InputText>

        <InputRadioGroupVar
            name="alcohol_drug_dependent"
            :required="checkIsRequired('alcohol_drug_dependent')"
            :error="formRef.errors.get('alcohol_drug_dependent')"
            v-model="formRef.alcohol_drug_dependent"
            :options="getField('alcohol_drug_dependent')['options']"
            @input="formRef.errors.clear('alcohol_drug_dependent')"
        >
            <template v-slot:question>
                {{ getField("alcohol_drug_dependent").question }}
            </template>
        </InputRadioGroupVar>
    </div>
</template>

<script setup>
import InputRadioGroup from "../../../Form/InputRadioGroup";
import InputCheckboxGroup from "../Form/InputCheckboxGroup";
import InputText from "../../../Form/InputText";
import Form from "../../../../Form";
import { isRequired } from "./formHelper.js";
import { ref, shallowRef, markRaw } from "vue";

const props = defineProps({
    step: Object,
    fields: Object,
    formData: Object
})

const InputRadioGroupVar = shallowRef(InputRadioGroup)

let formRef = markRaw(ref(new Form({
    any_illness: [],
    any_illness_reasons: null,
    illness_reason_disability_text: null,
    illness_reason_illness_text: null,
    illness_reason_mental_health_psychological_text: null,
    illness_reason_other_text: null,
    alcohol_drug_dependent: null,
    other_relation_text: null
}, props.formData)))

const getField = (fieldName) => {
   return props.fields[fieldName];
}

const checkIsRequired = (fieldName) => {
   var fieldData = getField(fieldName);
   return isRequired(fieldData, formRef.value);
}

function handleAffectedByInput(values) {
    if (values.length && (values[values.length - 1] === 'none')) {
        formRef.any_illness = [values[values.length - 1]];
        return;
    }
   clearCheckboxValues(['none'], values, 'any_illness');
}

function clearCheckboxValues(previousAnswers, values, formField) { 
    for (const answer of previousAnswers) {
        if (values.includes(answer)) {
            formRef.value[formField] = values.filter(value => value !== answer);
        }
    }
}

defineExpose({
 formRef
})
</script>